import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { FileCheck, Receipt, FileText, UserCheck, CircleDollarSign, Sparkles, Building2, CreditCard, Home, TrendingUp, Play } from 'lucide-react';
const badges = [{
  icon: FileCheck,
  label: 'Easy Application'
}, {
  icon: Receipt,
  label: 'No Hidden Fees'
}, {
  icon: FileText,
  label: 'No Payments Option'
}, {
  icon: UserCheck,
  label: 'No Age Requirement'
}, {
  icon: CircleDollarSign,
  label: 'Tax Free Money'
}];
const bulletPoints = [{
  icon: Sparkles,
  text: 'Enhance Your Lifestyle'
}, {
  icon: Building2,
  text: 'Leaving Inheritance to Your Loved Ones'
}, {
  icon: CreditCard,
  text: 'Eliminate Expensive Debt'
}, {
  icon: Home,
  text: 'Renovate or Build A Secondary Suite'
}, {
  icon: TrendingUp,
  text: 'Managing Divorce or Separation Equalization Payment'
}, {
  icon: FileText,
  text: 'Invest in Profitable Opportunities'
}];
// Animation Variants
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15
    }
  }
};
const badgeVariants = {
  hidden: {
    opacity: 0,
    scale: 0.8,
    y: 20
  },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: {
      type: 'spring',
      stiffness: 100,
      damping: 15
    }
  }
};
const slideRightVariants = {
  hidden: {
    opacity: 0,
    x: -50
  },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.6,
      ease: 'easeOut'
    }
  }
};
const slideLeftVariants = {
  hidden: {
    opacity: 0,
    x: 50
  },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.6,
      ease: 'easeOut',
      delay: 0.3
    }
  }
};
const bulletVariants = {
  hidden: {
    opacity: 0,
    x: -20
  },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.4
    }
  }
};
export function WealthAccessSection() {
  return <section className="w-full bg-white py-20 px-4 overflow-hidden" data-id="element-282">
      <div className="max-w-7xl mx-auto" data-id="element-283">
        {/* PART 1: Feature Badges Row */}
        <motion.div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8 mb-24" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-50px'
      }} data-id="element-284">
          {badges.map((badge, index) => <motion.div key={index} className="flex flex-col items-center text-center" variants={badgeVariants} data-id="element-285">
              <div className="w-20 h-20 rounded-full bg-[#f0f0f0] flex items-center justify-center mb-4 transition-transform hover:scale-105 duration-300" data-id="element-286">
                <badge.icon className="w-8 h-8 text-[#07493a]" strokeWidth={1.5} data-id="element-287" />
              </div>
              <span className="font-bold text-gray-900 text-sm md:text-base" data-id="element-288">
                {badge.label}
              </span>
            </motion.div>)}
        </motion.div>

        {/* PART 2: 'Access the Wealth You've Built' Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-289">
          {/* Left Column: Text & Bullets */}
          <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-50px'
        }} data-id="element-290">
            {/* Decorative Dots */}

            <motion.h2 className="text-3xl md:text-4xl lg:text-5xl font-serif text-[#07493a] mb-6 font-[500] leading-tight" variants={slideRightVariants} data-id="element-291">
              Access the Wealth You've Built
            </motion.h2>

            <motion.p className="text-lg text-gray-600 mb-8 leading-relaxed" variants={slideRightVariants} data-id="element-292">
              Whether you are at your pre-retirement or retired, finance your
              everyday life as you wish. All while you maintain ownership of
              your property.
            </motion.p>

            <div className="space-y-5" data-id="element-293">
              {bulletPoints.map((bullet, index) => <motion.div key={index} className="flex items-center" variants={bulletVariants} data-id="element-294">
                  <bullet.icon className="w-6 h-6 text-[#07493a] mr-4 flex-shrink-0" strokeWidth={2} data-id="element-295" />
                  <span className="font-bold text-gray-800 text-base md:text-lg" data-id="element-296">
                    {bullet.text}
                  </span>
                </motion.div>)}
            </div>
          </motion.div>

          {/* Right Column: Video Placeholder */}
          <motion.div className="relative w-full aspect-[4/3] md:aspect-video lg:aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl group cursor-pointer" variants={slideLeftVariants} initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-50px'
        }} data-id="element-297">
            {/* Poster Image */}
            <img src="https://images.unsplash.com/photo-1573497620053-ea5300f94f21?w=1200&q=80" alt="Elderly couple reviewing documents" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-298" />

            {/* Overlay Gradient for better play button visibility */}
            <div className="absolute inset-0 bg-black/20 transition-opacity duration-300 group-hover:bg-black/30" data-id="element-299" />

            {/* Play Button */}
            <div className="absolute inset-0 flex items-center justify-center" data-id="element-300">
              <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center shadow-xl transform transition-transform duration-300 group-hover:scale-110" data-id="element-301">
                <Play className="w-8 h-8 text-[#07493a] ml-1" fill="currentColor" data-id="element-302" />
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}